/* eslint-disable react/prop-types */
import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import * as assets from "./assets";
import jaagaLogo from "../Navbar/Group.webp";
import { redirectToWhatsApp } from "../../utils/functions/redirectToWhatsApp";
import { supportMailUrl } from "../../utils/functions/supportMail";
import { JAAGA_MAPS_URL } from "../../utils/constants";
import PolicyModal from "./PolicyModal";

// Lazy-loaded so they only download when a legal modal is opened, and so the
// static import cycle (these policies import Footer) never forms.
const TermsConditions = lazy(() => import("./Policies/TermsConditions"));
const PrivacyPolicies = lazy(() => import("./Policies/PrivacyPolicies"));
const RefundCancellationPolicy = lazy(() =>
  import("./Policies/RefundCancellationPolicy")
);
const ShippingDeliveryPolicy = lazy(() =>
  import("./Policies/ShippingDeliveryPolicy")
);

const SERIF = "'DM Sans', sans-serif";

// Order mirrors the web header: Documents → Services → Intelligence → Maps
// (Maps sits next to Intelligence), then About.
const EXPLORE = [
  { label: "Property Documents", to: "/states" },
  { label: "Property Services", to: "/services" },
  // { label: "Intelligence", to: "/intelligence" }, // hidden with JaaGa Intelligence
  { label: "Survey Maps", to: "/maps?tab=survey" },
  { label: "Calculators", to: "/calculators" },
  { label: "About", to: "/about" },
];

// Legal items render real routes (crawlable hrefs for SEO) but still open the
// modal via onClick so visitors never leave the page.
const LEGAL = [
  { label: "Terms & Conditions", key: "terms", to: "/terms" },
  { label: "Privacy Policy", key: "privacy", to: "/privacy-policy" },
  {
    label: "Cancellation & Refund",
    key: "refund",
    to: "/refund-cancellation-policy",
  },
  // NB: the live route really is spelled with three p's.
  {
    label: "Shipping & Delivery",
    key: "shipping",
    to: "/shipping-delivery-policy",
  },
];

// High-intent document pages, footer-wide internal links for SEO crawl paths.
const POPULAR_DOCUMENTS = [
  { label: "Telangana EC (Encumbrance Certificate)", to: "/telangana/ec" },
  { label: "AP Adangal (MeeBhoomi)", to: "/andhra-pradesh/adangal" },
  { label: "Tamil Nadu Patta Chitta", to: "/tamil-nadu/patta-chitta" },
  { label: "Karnataka RTC / Pahani", to: "/karnataka/rtc-pahani" },
  { label: "Maharashtra 7/12 Satbara", to: "/maharashtra/satbara" },
  { label: "Telangana Sale Deed", to: "/telangana/sale-deed" },
  { label: "AP Encumbrance Certificate", to: "/andhra-pradesh/ec" },
  { label: "Karnataka EC", to: "/karnataka/ec" },
  // Survey map pages (pages/landTools) — a third row of 4, so the grid stays even.
  { label: "Survey Number Map", to: "/survey-number-map" },
  { label: "Telangana Survey Number Map", to: "/telangana/survey-number-map" },
  { label: "AP Survey Number Map", to: "/andhra-pradesh/survey-number-map" },
  { label: "Draw Land Map Online", to: "/draw-land-map" },
];

const POPULAR_SERVICES = [
  // Points at /ptin, not /property-tax: PTIN Creation is the service that
  // actually handles property tax, and it's the one with a published config
  // (all-services/property-services/ptin/index.json). The property-tax folder
  // has only a `form` and no index.json, so /property-tax renders an empty
  // service page.
  { label: "Property Tax", to: "/ptin" },
  {
    label: "Legal Opinion / Title Verification",
    to: "/title-verification-report",
  },
  { label: "Property Valuation", to: "/property-valuation-service" },
  { label: "Mutation", to: "/mutation-creation" },
];

// `key` → { title, Component } for the legal modals.
const POLICIES = {
  terms: { title: "Terms & Conditions", Component: TermsConditions },
  privacy: { title: "Privacy Policy", Component: PrivacyPolicies },
  refund: { title: "Cancellation & Refund", Component: RefundCancellationPolicy },
  shipping: { title: "Shipping & Delivery", Component: ShippingDeliveryPolicy },
};

const SOCIALS = [
  { src: assets.linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/jaagarealestate/about/" },
  { src: assets.facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61552907515347&mibextid=LQQJ4d" },
  { src: assets.x, label: "Twitter", href: "https://x.com/Jaaga_ai" },
  { src: assets.instagram, label: "Instagram", href: "https://www.instagram.com/jaaga.ai/" },
  { src: assets.youtube, label: "Youtube", href: "https://www.youtube.com/@jaagaapp" },
];

// Row of social icons + WhatsApp, reused at the top (desktop) and bottom (mobile).
const SocialIcons = ({ onWhatsApp }) => (
  <div className="flex items-center gap-2.5">
    {SOCIALS.map((s) => (
      <Link
        key={s.label}
        to={s.href}
        target="_blank"
        rel="noreferrer"
        aria-label={s.label}
        className="grid h-9 w-9 place-content-center rounded-full bg-white/5 ring-1 ring-white/10 transition hover:bg-white/10 hover:ring-[#1c4b93]/40"
      >
        <img src={s.src} alt={s.label} width={18} height={18} className="h-[18px] w-[18px]" />
      </Link>
    ))}
    <button
      type="button"
      onClick={onWhatsApp}
      aria-label="WhatsApp"
      className="grid h-9 w-9 place-content-center rounded-full bg-white/5 ring-1 ring-white/10 transition hover:bg-white/10 hover:ring-[#1c4b93]/40"
    >
      <img src={assets.whatsapp} alt="WhatsApp" width={18} height={18} className="h-[18px] w-[18px]" />
    </button>
  </div>
);

// Small reusable link with a gold underline that grows on hover.
const FootLink = ({ to, children, external, onClick }) => {
  const cls =
    "group inline-flex w-fit items-center text-left text-[13px] text-white/70 transition-colors hover:text-white";
  const underline = (
    <span className="ml-2 h-px w-0 bg-[#f0a500] transition-all duration-300 group-hover:w-3.5" />
  );
  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
      {underline}
    </a>
  ) : (
    <Link to={to} onClick={onClick} className={cls}>
      {children}
      {underline}
    </Link>
  );
};

const Footer = ({ showTop = true } = {}) => {
  const emailHref = supportMailUrl();
  // Which legal policy modal is open (null = none).
  const [policy, setPolicy] = useState(null);
  const active = policy ? POLICIES[policy] : null;

  const handleWhatsAppRedirect = () =>
    redirectToWhatsApp({
      phone: "918885166002",
      message: "Want to know More About JaaGa",
    });

  return (
    <footer className="relative w-full overflow-hidden bg-[#071a3c] text-white">
      {/* atmospheric depth, a faint gold glow + deep-navy wash */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0c2655] via-[#081e45] to-[#040f29]" />
      <span className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-[#1c4b93]/10 blur-3xl" />
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#f0a500]/60 to-transparent" />

      <div className="relative mx-auto max-w-screen-xl px-5 sm:px-8">
        {showTop && (
          <>
            {/* ── Main columns ─────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-8 py-9 lg:grid-cols-12">
              {/* Brand + about + social */}
              <div className="col-span-2 lg:col-span-3">
                {/* The SAME brand lockup as the navbar — mark on top, tagline
                    indented to start under the "J" and justified to the
                    wordmark's right edge — so the two read as one logo rather
                    than two different ones.

                    `items-start` is load-bearing: a flex COLUMN stretches its
                    children to the widest one by default, which pulled the
                    182px-wide tagline's width onto the image and rendered the
                    264×82 mark at 182×36 — a wordmark stretched half as wide
                    again as it should be. */}
                <Link
                  to="/"
                  className="inline-flex flex-col items-start leading-none"
                  style={{ fontFamily: SERIF }}
                >
                  {/* JaaGa logo — web + mobile. Width/height only supply the
                      3.23:1 ratio; the height is pinned by `h-11`. */}
                  <img
                    src={jaagaLogo}
                    alt="JaaGa"
                    width={1205}
                    height={373}
                    className="h-11 w-auto brightness-0 invert"
                  />
                  <span className="-mt-1 ml-[33px] block w-[109px] text-[9px] font-semibold text-[#f0a500] [text-align-last:justify]">
                    Everything For Property
                  </span>
                </Link>
                <p className="mt-3 max-w-sm text-[13px] leading-relaxed text-white/60">
                  India&apos;s most complete platform for land &amp; property,
                  140+ instant documents, AI risk scores, survey maps and
                  lawyer-signed legal opinions, in one secure place.
                </p>
                {/* Socials: shown here on desktop (moved to the bottom on mobile). */}
                <div className="mt-5 hidden sm:block">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                    Follow Us
                  </p>
                  <SocialIcons onWhatsApp={handleWhatsAppRedirect} />
                </div>
              </div>

              {/* Explore */}
              <div className="hidden lg:col-span-2 lg:block">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                  Explore
                </p>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {EXPLORE.map((l) => (
                    <li key={l.label}>
                      <FootLink to={l.to}>{l.label}</FootLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal — real hrefs for crawlers, modal UX for visitors */}
              <div className="col-span-2 lg:col-span-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                  Legal
                </p>
                <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 lg:flex lg:flex-col lg:gap-2.5">
                  {LEGAL.map((l) => (
                    <li key={l.key}>
                      <FootLink
                        to={l.to}
                        onClick={(e) => {
                          e.preventDefault();
                          setPolicy(l.key);
                        }}
                      >
                        {l.label}
                      </FootLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Popular Services — its own column so every column is
                  top-aligned and the same height (no stacked, ragged column). */}
              <div className="hidden lg:col-span-2 lg:block">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                  Popular Services
                </p>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {POPULAR_SERVICES.map((l) => (
                    <li key={l.label}>
                      <FootLink to={l.to}>{l.label}</FootLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Reach us */}
              <div className="col-span-2 lg:col-span-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                  Reach us
                </p>
                <ul className="mt-4 space-y-3 text-[13px] text-white/70">
                  <li>
                    <a
                      href={JAAGA_MAPS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2.5 transition-colors hover:text-white"
                    >
                      <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#f0a500]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="leading-relaxed">
                        16th Floor, Orbit, Hyderabad Knowledge City, Raidurg,
                        Serilingampally, Hyderabad, Telangana 500019, India
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={emailHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 transition-colors hover:text-white"
                    >
                      <svg className="h-4 w-4 shrink-0 text-[#f0a500]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      contact@jaaga.ai
                    </a>
                  </li>
                  <li>
                    <a href="https://wa.me/+918885166002" className="flex items-center gap-2.5 transition-colors hover:text-white">
                      <svg className="h-4 w-4 shrink-0 text-[#f0a500]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      +91 88851 66002
                    </a>
                  </li>
                </ul>
              </div>

              {/* Popular Documents — site-wide internal links for SEO */}
              <div className="hidden col-span-2 lg:col-span-12 lg:block">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                  Popular Documents &amp; Maps
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-4">
                  {POPULAR_DOCUMENTS.map((l) => (
                    <li key={l.label}>
                      <FootLink to={l.to}>{l.label}</FootLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {/* Follow Us — moved to the bottom on mobile (like MyPatta). */}
            <div className="border-t border-white/10 py-6 sm:hidden">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                Follow Us
              </p>
              <div className="mt-4">
                <SocialIcons onWhatsApp={handleWhatsAppRedirect} />
              </div>
            </div>
          </>
        )}

        {/* ── Bottom bar ─────────────────────────────────────────────────── */}
        <div className="flex flex-col items-center gap-2.5 border-t border-white/10 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-[12px] text-white/50">
            &copy; 2026 JaaGa Real Estate Services Pvt. Ltd. · All rights
            reserved.
          </p>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/35">
            Crafted in India · Trusted worldwide
          </p>
        </div>
      </div>

      {/* Legal policy modal, opens in place instead of navigating away. */}
      <PolicyModal
        open={!!active}
        title={active?.title}
        onClose={() => setPolicy(null)}
      >
        {active && (
          <Suspense
            fallback={
              <div className="p-12 text-center text-sm text-slate-400">
                Loading…
              </div>
            }
          >
            <active.Component modal />
          </Suspense>
        )}
      </PolicyModal>
    </footer>
  );
};

export default Footer;
